import React, { useContext, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Chip from '@material-ui/core/Chip';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContentText from '@material-ui/core/DialogContentText';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DescriptionIcon from '@material-ui/icons/Description';
import DeleteIcon from '@material-ui/icons/Delete';
import UnsubscribeIcon from '@material-ui/icons/Unsubscribe';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import SendIcon from '@material-ui/icons/Send';
import CancelIcon from '@material-ui/icons/Cancel';
import ScheduleIcon from '@material-ui/icons/Schedule';
import UpdateIcon from '@material-ui/icons/Update';
import PublicIcon from '@material-ui/icons/Public';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import EventIcon from '@material-ui/icons/Event';
import ListIcon from '@material-ui/icons/List';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(2),
    },
  },
  headerButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
      '& .MuiButton-root': {
        width: '100%',
        justifyContent: 'center',
      },
    },
  },
  searchBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      overflowX: 'auto',
    },
  },
  table: {
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[100],
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 800,
      '& .MuiTableCell-root': {
        padding: theme.spacing(1),
        fontSize: '0.75rem',
      },
    },
  },
  statusChip: {
    fontWeight: 500,
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  previewDialog: {
    '& .MuiDialog-paper': {
      maxWidth: '800px',
      width: '100%',
    },
  },
  previewContent: {
    minHeight: '400px',
    '& iframe': {
      border: 'none',
      width: '100%',
      minHeight: '500px',
    },
  },
}));

const EmailCampaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, token } = useContext(AuthContext);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [campaignToReschedule, setCampaignToReschedule] = useState(null);
  const [newScheduledTime, setNewScheduledTime] = useState('');
  const [rescheduleTimezone, setRescheduleTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}email-campaigns`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const localTime = date.toLocaleString('en-US', { 
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    const utcTime = date.toLocaleString('en-US', { 
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    const easternTime = date.toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    // Only add user timezone if it's different from UTC and Eastern
    let userTimezoneFormatted = null;
    let userTimezoneName = null;
    if (userTimezone !== 'UTC' && userTimezone !== 'America/New_York') {
      userTimezoneFormatted = date.toLocaleString('en-US', { 
        timeZone: userTimezone,
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      // Extract a friendly name for the timezone
      userTimezoneName = userTimezone.split('/').pop().replace(/_/g, ' ');
    }

    return {
      local: localTime,
      utc: utcTime,
      eastern: easternTime,
      userTimezone: userTimezoneFormatted,
      userTimezoneName: userTimezoneName
    };
  };

  const formatScheduledTime = (dateString) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const localTime = date.toLocaleString('en-US', { 
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    // Format in multiple timezones for clarity
    const utcTime = date.toLocaleString('en-US', { 
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    const easternTime = date.toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    // Only add user timezone if it's different from UTC and Eastern
    let userTimezoneFormatted = null;
    let userTimezoneName = null;
    if (userTimezone !== 'UTC' && userTimezone !== 'America/New_York') {
      userTimezoneFormatted = date.toLocaleString('en-US', { 
        timeZone: userTimezone,
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      // Extract a friendly name for the timezone
      userTimezoneName = userTimezone.split('/').pop().replace(/_/g, ' ');
    }

    return {
      local: localTime,
      utc: utcTime,
      eastern: easternTime,
      userTimezone: userTimezoneFormatted,
      userTimezoneName: userTimezoneName
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'scheduled':
        return 'primary';
      case 'sent':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Filter campaigns based on search term
  const filteredCampaigns = campaigns.filter(campaign => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      campaign.name?.toLowerCase().includes(term) ||
      campaign.subject?.toLowerCase().includes(term) ||
      campaign.status?.toLowerCase().includes(term)
    );
  });

  // Sort campaigns
  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    // Convert to numbers for numeric fields
    if (orderBy === 'recipient_count' || orderBy === 'opens') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
    }

    // Convert dates for comparison
    if (orderBy === 'created_at' || orderBy === 'sent_at') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    if (orderDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  const handleViewDetails = (campaignId) => {
    history.push(`/email-campaigns/${campaignId}/details`);
  };

  const handlePreview = async (campaign) => {
    try {
      // Fetch full campaign data if not already loaded
      const response = await axios.get(`${API_URL}email-campaigns/${campaign.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreviewCampaign(response.data.campaign);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error fetching campaign:', error);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewCampaign(null);
  };

  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API_URL}email-campaigns/${campaignToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setSnackbar({
        open: true,
        message: `Campaign "${campaignToDelete.name}" deleted successfully`,
        severity: 'success',
      });
      
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      fetchCampaigns(); // Refresh list
    } catch (error) {
      console.error('Error deleting campaign:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete campaign',
        severity: 'error',
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
  };

  const handleEditDraft = (campaignId) => {
    history.push(`/email-campaigns/edit/${campaignId}`);
  };

  const handleCancelScheduled = async (campaign) => {
    if (!window.confirm(`Are you sure you want to cancel the scheduled campaign "${campaign.name}"?`)) {
      return;
    }

    try {
      await axios.post(`${API_URL}email-campaigns/${campaign.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setSnackbar({
        open: true,
        message: `Campaign "${campaign.name}" cancelled successfully`,
        severity: 'success',
      });
      
      fetchCampaigns(); // Refresh list
    } catch (error) {
      console.error('Error cancelling campaign:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to cancel campaign',
        severity: 'error',
      });
    }
  };

  const handleRescheduleClick = (campaign) => {
    setCampaignToReschedule(campaign);
    // Pre-fill with current scheduled time if available
    if (campaign.scheduled_send_time) {
      const date = new Date(campaign.scheduled_send_time);
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setNewScheduledTime(localDateTime);
    }
    setRescheduleDialogOpen(true);
  };

  const handleRescheduleConfirm = async () => {
    if (!newScheduledTime) {
      setSnackbar({
        open: true,
        message: 'Please select a new scheduled time',
        severity: 'error',
      });
      return;
    }

    try {
      // Parse time in selected timezone and convert to UTC
      const [datePart, timePart] = newScheduledTime.split('T');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      
      // Create date in selected timezone
      const scheduledDate = new Date(
        Date.UTC(year, month - 1, day, hour, minute) - 
        (new Date().getTimezoneOffset() * 60000)
      );

      // Validation
      const now = new Date();
      if (scheduledDate <= now) {
        setSnackbar({
          open: true,
          message: `Scheduled send time must be in the future. Current time: ${now.toLocaleString('en-US', { 
            timeZone: rescheduleTimezone
          })} (${rescheduleTimezone})`,
          severity: 'error',
        });
        return;
      }

      // Show confirmation with both timezones
      const localTimeStr = scheduledDate.toLocaleString('en-US', { 
        timeZone: rescheduleTimezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const utcTimeStr = scheduledDate.toLocaleString('en-US', { 
        timeZone: 'UTC',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });

      if (!window.confirm(
        `Reschedule "${campaignToReschedule.name}"?\n\n` +
        `📅 New Time in ${rescheduleTimezone}:\n${localTimeStr}\n\n` +
        `🌍 Server Time (UTC):\n${utcTimeStr}\n\n` +
        `The campaign will be sent at the ${rescheduleTimezone} time shown above.`
      )) {
        return;
      }

      await axios.post(
        `${API_URL}email-campaigns/${campaignToReschedule.id}/reschedule`,
        { scheduledSendTime: scheduledDate.toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({
        open: true,
        message: `Campaign "${campaignToReschedule.name}" rescheduled successfully`,
        severity: 'success',
      });

      setRescheduleDialogOpen(false);
      setCampaignToReschedule(null);
      setNewScheduledTime('');
      fetchCampaigns();
    } catch (error) {
      console.error('Error rescheduling campaign:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to reschedule campaign',
        severity: 'error',
      });
    }
  };

  const handleRescheduleCancel = () => {
    setRescheduleDialogOpen(false);
    setCampaignToReschedule(null);
    setNewScheduledTime('');
  };

  const handleSendCancelledCampaign = async (campaign) => {
    if (!window.confirm(`Send campaign "${campaign.name}" immediately?`)) {
      return;
    }

    try {
      // Reactivate and send immediately by rescheduling to NOW
      await axios.post(
        `${API_URL}email-campaigns/${campaign.id}/reschedule`,
        { scheduledSendTime: new Date().toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({
        open: true,
        message: `Campaign "${campaign.name}" is being sent`,
        severity: 'success',
      });

      fetchCampaigns();
    } catch (error) {
      console.error('Error sending campaign:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to send campaign',
        severity: 'error',
      });
    }
  };


  const handleManageUnsubscribes = () => {
    history.push('/email-campaigns/unsubscribes');
  };

  const handleManageRecipientLists = () => {
    history.push('/email-campaigns/recipient-lists');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">Email Campaign System</Typography>
        <Box className={classes.headerButtons}>
          <Button
            variant="outlined"
            startIcon={<ListIcon />}
            onClick={handleManageRecipientLists}
          >
            Recipient Lists
          </Button>
          <Button
            variant="outlined"
            startIcon={<UnsubscribeIcon />}
            onClick={handleManageUnsubscribes}
          >
            Unsubscribes
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => history.push('/email-campaigns/create')}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {isLoading && <LinearProgress />}

      <Paper className={classes.searchBox}>
        <Box p={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search campaigns by name, subject, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <Paper className={classes.tableContainer}>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? orderDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Campaign Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'subject'}
                    direction={orderBy === 'subject' ? orderDirection : 'asc'}
                    onClick={() => handleSort('subject')}
                  >
                    Subject
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? orderDirection : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'recipient_count'}
                    direction={orderBy === 'recipient_count' ? orderDirection : 'asc'}
                    onClick={() => handleSort('recipient_count')}
                  >
                    Recipients
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'opens'}
                    direction={orderBy === 'opens' ? orderDirection : 'asc'}
                    onClick={() => handleSort('opens')}
                  >
                    Opens
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? orderDirection : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'sent_at'}
                    direction={orderBy === 'sent_at' ? orderDirection : 'asc'}
                    onClick={() => handleSort('sent_at')}
                  >
                    Sent
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'scheduled_send_time'}
                    direction={orderBy === 'scheduled_send_time' ? orderDirection : 'asc'}
                    onClick={() => handleSort('scheduled_send_time')}
                  >
                    Scheduled For
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCampaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{campaign.subject}</TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.status}
                      size="small"
                      className={classes.statusChip}
                      color={getStatusColor(campaign.status)}
                    />
                  </TableCell>
                  <TableCell>{campaign.recipient_count || 0}</TableCell>
                  <TableCell>{campaign.opens || 0}</TableCell>
                  <TableCell>
                    {formatDate(campaign.created_at) ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" display="block">
                              <strong>UTC:</strong> {formatDate(campaign.created_at).utc}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Eastern:</strong> {formatDate(campaign.created_at).eastern}
                            </Typography>
                            {formatDate(campaign.created_at).userTimezone && (
                              <Typography variant="caption" display="block">
                                <strong>{formatDate(campaign.created_at).userTimezoneName}:</strong> {formatDate(campaign.created_at).userTimezone}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="left"
                      >
                        <Box>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {formatDate(campaign.created_at).local}
                          </Typography>
                          <Typography variant="caption" style={{ color: '#757575', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <PublicIcon style={{ fontSize: '0.75rem' }} /> UTC: {formatDate(campaign.created_at).utc}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDate(campaign.sent_at) ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" display="block">
                              <strong>UTC:</strong> {formatDate(campaign.sent_at).utc}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Eastern:</strong> {formatDate(campaign.sent_at).eastern}
                            </Typography>
                            {formatDate(campaign.sent_at).userTimezone && (
                              <Typography variant="caption" display="block">
                                <strong>{formatDate(campaign.sent_at).userTimezoneName}:</strong> {formatDate(campaign.sent_at).userTimezone}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="left"
                      >
                        <Box>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {formatDate(campaign.sent_at).local}
                          </Typography>
                          <Typography variant="caption" style={{ color: '#757575', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <PublicIcon style={{ fontSize: '0.75rem' }} /> UTC: {formatDate(campaign.sent_at).utc}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {campaign.scheduled_send_time ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" display="block">
                              <strong>UTC:</strong> {formatScheduledTime(campaign.scheduled_send_time).utc}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Eastern:</strong> {formatScheduledTime(campaign.scheduled_send_time).eastern}
                            </Typography>
                            {formatScheduledTime(campaign.scheduled_send_time).userTimezone && (
                              <Typography variant="caption" display="block">
                                <strong>{formatScheduledTime(campaign.scheduled_send_time).userTimezoneName}:</strong> {formatScheduledTime(campaign.scheduled_send_time).userTimezone}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="left"
                      >
                        <Box>
                          <Typography variant="body2" style={{ color: '#ff9800', fontWeight: 500 }}>
                            {formatScheduledTime(campaign.scheduled_send_time).local}
                          </Typography>
                          <Typography variant="caption" style={{ color: '#757575', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <PublicIcon style={{ fontSize: '0.75rem' }} /> UTC: {formatScheduledTime(campaign.scheduled_send_time).utc}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Box className={classes.actionButtons}>
                      {/* View Details - always available */}
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(campaign.id)}
                        >
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* Preview - always available */}
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(campaign)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* Edit - only for drafts */}
                      {campaign.status === 'draft' && (
                        <Tooltip title="Edit Draft">
                          <IconButton
                            size="small"
                            onClick={() => handleEditDraft(campaign.id)}
                            style={{ color: '#2196f3' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Reschedule - for scheduled campaigns */}
                      {campaign.status === 'scheduled' && (
                        <Tooltip title="Reschedule">
                          <IconButton
                            size="small"
                            onClick={() => handleRescheduleClick(campaign)}
                            style={{ color: '#2196f3' }}
                          >
                            <UpdateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Cancel - only for scheduled */}
                      {campaign.status === 'scheduled' && (
                        <Tooltip title="Cancel Scheduled Send">
                          <IconButton
                            size="small"
                            onClick={() => handleCancelScheduled(campaign)}
                            style={{ color: '#ff9800' }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Reschedule and Send Now - for cancelled campaigns */}
                      {campaign.status === 'cancelled' && (
                        <>
                          <Tooltip title="Reschedule">
                            <IconButton
                              size="small"
                              onClick={() => handleRescheduleClick(campaign)}
                              style={{ color: '#2196f3' }}
                            >
                              <ScheduleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send Now">
                            <IconButton
                              size="small"
                              onClick={() => handleSendCancelledCampaign(campaign)}
                              style={{ color: '#4caf50' }}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* Delete - always available */}
                      <Tooltip title="Delete Campaign">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(campaign)}
                          style={{ color: '#f44336' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {sortedCampaigns.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchTerm
                        ? 'No campaigns found matching your search.'
                        : 'No campaigns found. Click "Create Campaign" to get started!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        className={classes.previewDialog}
        maxWidth="md"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {previewCampaign?.name || 'Campaign Preview'}
            </Typography>
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.previewContent}>
          {previewCampaign && (
            <>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Subject:</Typography>
                <Typography variant="body1">{previewCampaign.subject}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">From:</Typography>
                <Typography variant="body1">
                  {previewCampaign.from_name} &lt;{previewCampaign.from_email}&gt;
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Email Preview:
                </Typography>
                <Paper elevation={1} style={{ padding: '16px' }}>
                  <div dangerouslySetInnerHTML={{ __html: previewCampaign.body }} />
                </Paper>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the campaign "{campaignToDelete?.name}"?
            This action cannot be undone and will also delete all associated recipient
            data, opens, and clicks.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="secondary" 
            variant="contained"
            style={{ backgroundColor: '#f44336' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog
        open={rescheduleDialogOpen}
        onClose={handleRescheduleCancel}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle>Reschedule Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose a new send time for "{campaignToReschedule?.name}".
          </DialogContentText>
          
          {/* Info box with detected timezone */}
          <Paper style={{ backgroundColor: '#e3f2fd', padding: '12px', marginTop: '16px', marginBottom: '16px' }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1976d2' }}>
              <AccessTimeIcon style={{ fontSize: '1.2rem' }} />
              <Typography variant="body2">
                <strong>Your Current Timezone: {rescheduleTimezone}</strong>
              </Typography>
            </Box>
            <Typography variant="caption" display="block" style={{ marginTop: '4px' }}>
              The system detected your timezone automatically. You can change it below if needed.
            </Typography>
          </Paper>

          {/* Timezone Selector */}
          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel>Timezone for Scheduling</InputLabel>
            <Select 
              value={rescheduleTimezone} 
              onChange={(e) => setRescheduleTimezone(e.target.value)}
              label="Timezone for Scheduling"
            >
              <MenuItem value="America/New_York">Eastern Time (US & Canada)</MenuItem>
              <MenuItem value="America/Chicago">Central Time (US & Canada)</MenuItem>
              <MenuItem value="America/Denver">Mountain Time (US)</MenuItem>
              <MenuItem value="America/Los_Angeles">Pacific Time (US & Canada)</MenuItem>
              <MenuItem value="Europe/London">London (GMT/BST)</MenuItem>
              <MenuItem value="Europe/Paris">Paris (CET/CEST)</MenuItem>
              <MenuItem value="Asia/Dubai">Dubai (GST)</MenuItem>
              <MenuItem value="Asia/Kuwait">Kuwait (AST)</MenuItem>
              <MenuItem value="Asia/Riyadh">Riyadh (AST)</MenuItem>
              <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
              <MenuItem value="Asia/Shanghai">Shanghai (CST)</MenuItem>
              <MenuItem value="Australia/Sydney">Sydney (AEST/AEDT)</MenuItem>
              <MenuItem value="UTC">UTC (Universal Time)</MenuItem>
            </Select>
          </FormControl>

          {/* DateTime Picker */}
          <TextField
            label={`New Scheduled Send Time (${rescheduleTimezone.split('/')[1] || 'UTC'} time)`}
            type="datetime-local"
            fullWidth
            value={newScheduledTime}
            onChange={(e) => setNewScheduledTime(e.target.value)}
            margin="normal"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            helperText={`Enter the time in ${rescheduleTimezone} timezone. The campaign will be sent at this exact time in ${rescheduleTimezone}. The system stores times in UTC internally and will send at the correct moment.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRescheduleCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleRescheduleConfirm} 
            color="primary" 
            variant="contained"
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EmailCampaigns;
